package com.schoolconnect.app.feature.exams

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.horizontalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TriStateCheckbox
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.state.ToggleableState
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import com.schoolconnect.app.feature.common.compareBySmartText
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.clipboard_check
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.health_chart
import ischoolconnect.sharedui.generated.resources.pencil
import ischoolconnect.sharedui.generated.resources.refresh_cw
import ischoolconnect.sharedui.generated.resources.user
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource
import kotlin.time.Clock

private fun examTypeDisplayValue(value: String): String =
    com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.label("EXAM_TYPE", value).ifBlank { value.trim() }

private data class ExamSubjectRowDraft(
    val name: String,
    val subjectId: String,
    val selected: Boolean,
    val internal: String = "",
    val main: String = "",
    val grade: Boolean = false,
    val rank: Boolean = false,
    val marks: Boolean = false
) {
    val total: String get() = if (!marks || (internal.isBlank() && main.isBlank())) "—" else (internal.toIntOrNull() ?: 0).plus(main.toIntOrNull() ?: 0).toString()
}

private fun buildExamDraft(
    name: String,
    type: String,
    selectedClasses: List<String>,
    startDate: String,
    endDate: String,
    status: String,
    classOptions: Map<String, String>,
    subjects: List<ExamSubjectRowDraft>,
    hasOverallGrade: Boolean,
    hasOverallRank: Boolean,
    hasOverallMarks: Boolean
): ExamDraft = ExamDraft(
    title = name.trim(),
        examType = type.trim(),
    classSectionIds = selectedClasses.mapNotNull { classOptions[it] },
    startDate = startDate,
    endDate = endDate,
    status = status,
    examId = "EX-${Clock.System.now().toEpochMilliseconds()}",
    subjectMarkConfigs = subjects.filter { subject ->
        subject.selected && ((subject.grade && hasOverallGrade) || (subject.rank && hasOverallRank) || (subject.marks && hasOverallMarks))
    }.map {
        val internal = it.internal.toDoubleOrNull() ?: 0.0
        val main = it.main.toDoubleOrNull() ?: 0.0
        ExamSubjectDraft(
            it.subjectId,
            internal.toString(),
            main.toString(),
            (internal + main).toString(),
            it.grade && hasOverallGrade,
            it.rank && hasOverallRank,
            it.marks && hasOverallMarks
        )
    },
    hasOverallGrade = hasOverallGrade,
    hasOverallRank = hasOverallRank,
    hasOverallMarks = hasOverallMarks
)

@Composable
internal fun AddExamDialog(
    visible: Boolean,
    initialExam: ExamRowUi? = null,
    onDismiss: () -> Unit,
    classOptions: Map<String, String> = emptyMap(),
    subjectOptions: Map<String, String> = emptyMap(),
    examTypeOptions: List<String> = emptyList(),
    statusOptions: List<String> = emptyList(),
    isSubjectsLoading: Boolean = false,
    isSaving: Boolean = false,
    errorMessage: String = "",
    onAdd: (ExamDraft) -> Unit,
    onEdit: (ExamRowUi) -> Unit = {},
    onClassSelected: (String) -> Unit = {}
) {
    var hasSubmitted by remember(initialExam?.id, visible) { mutableStateOf(false) }
    if (!visible) return

    val editing = initialExam != null
    var name by remember(initialExam?.id) { mutableStateOf(initialExam?.name.orEmpty()) }
    var type by remember(initialExam?.id) { mutableStateOf(examTypeDisplayValue(initialExam?.type.orEmpty())) }
    var selectedClasses by remember(initialExam?.id, classOptions.keys.toList()) {
        mutableStateOf(
            initialExam?.classes?.split(", ").orEmpty().filter { it.isNotBlank() }
                .ifEmpty { listOfNotNull(classOptions.keys.sortedWith(compareBySmartText<String> { it }).firstOrNull()) }
        )
    }
    var status by remember(initialExam?.id, statusOptions) { mutableStateOf(statusOptions.firstOrNull().orEmpty()) }
    var startDate by remember(initialExam?.id) { mutableStateOf(initialExam?.startDate.orEmpty()) }
    var endDate by remember(initialExam?.id) { mutableStateOf(initialExam?.endDate.orEmpty()) }
    var classExpanded by remember { mutableStateOf(false) }
    var statusExpanded by remember { mutableStateOf(false) }
    var overallGrade by remember(initialExam?.id) { mutableStateOf(initialExam?.hasOverallGrade ?: true) }
    var overallRank by remember(initialExam?.id) { mutableStateOf(initialExam?.hasOverallRank ?: true) }
    var overallMarks by remember(initialExam?.id) { mutableStateOf(initialExam?.hasOverallMarks ?: true) }
    var subjects by remember(initialExam?.id, subjectOptions) { mutableStateOf(emptyList<ExamSubjectRowDraft>()) }
    var bulkTotalMarks by remember(initialExam?.id) { mutableStateOf("") }
    val nameError = if (hasSubmitted && name.isBlank()) "Exam name is required." else ""
    val typeError = if (hasSubmitted && type.isBlank()) "Exam type is required." else ""
    val startDateError = if (hasSubmitted && startDate.isBlank()) "Start date is required." else ""
    val endDateError = if (hasSubmitted && endDate.isBlank()) "End date is required." else ""
    val formValid = listOf(nameError, typeError, startDateError, endDateError).all { it.isBlank() }
    LaunchedEffect(selectedClasses.joinToString("|"), subjectOptions, initialExam?.id) {
        val availableSubjects = subjectOptions.entries.map { it.key to it.value }
        subjects = availableSubjects.mapIndexed { index, subject ->
            val saved = initialExam?.subjectConfigs?.get(subject.second)
            ExamSubjectRowDraft(
                name = subject.first,
                subjectId = subject.second,
                selected = saved != null && (saved.hasGrade || saved.hasRank || saved.hasMarks),
                internal = saved?.internalMark.orEmpty(),
                main = saved?.mainMark.orEmpty().toDoubleOrNull()?.takeIf { it > 0.0 }?.toString()?.removeSuffix(".0")
                    ?: saved?.totalMark.orEmpty(),
                grade = saved?.hasGrade == true,
                rank = saved?.hasRank == true,
                marks = saved?.hasMarks == true
            )
        }
    }

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, maxDialogHeight = 1000.dp, isLoading = isSaving) {
        AppDialogHeader(title = if (editing) "Edit Exam Details" else "Add Exam Details", subtitle = if (editing) "Update the exam and its result configuration." else "Create an exam and configure its result details.", icon = null, onClose = onDismiss, colors = AppDialogDefaultColors)
        AppDialogValidationSummary(errors = listOf(nameError, typeError, startDateError, endDateError), modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp))
        AppDialogErrorMessage(message = errorMessage, modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp))
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 28.dp, vertical = 20.dp), verticalArrangement = Arrangement.spacedBy(18.dp)) {
            ExamDialogSectionHeader("1", "Examination Details")
            AppDialogFormGrid(Modifier.fillMaxWidth()) { compact ->
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        ExamNameField(name, error = nameError) { name = it }
                        ExamTypeField(type, error = typeError) { type = it }
                        ExamDateField("Start Date", startDate, error = startDateError) { startDate = it }
                        ExamDateField("End Date", endDate, error = endDateError) { endDate = it }
                        ExamClassField(selectedClasses.firstOrNull().orEmpty(), classExpanded, classOptions.keys.sortedWith(compareBySmartText<String> { it }), { classExpanded = true }, { classExpanded = false }) { selectedClasses = listOf(it); onClassSelected(it) }
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) { ExamNameField(name, Modifier.weight(1f), nameError) { name = it }; ExamTypeField(type, modifier = Modifier.weight(1f), error = typeError) { type = it } }
                        Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) { ExamDateField("Start Date", startDate, Modifier.weight(1f), startDateError) { startDate = it }; ExamDateField("End Date", endDate, Modifier.weight(1f), endDateError) { endDate = it }; ExamClassField(selectedClasses.firstOrNull().orEmpty(), classExpanded, classOptions.keys.sortedWith(compareBySmartText<String> { it }), { classExpanded = true }, { classExpanded = false }, Modifier.weight(1f)) { selectedClasses = listOf(it); onClassSelected(it) } }
                    }
                }
            }

            AppDialogDivider()
            ExamDialogSectionHeader("2", "Result Configuration")
            Text("Choose how the overall result will be recorded and displayed.", color = AppDialogDefaultColors.muted, fontSize = 14.sp)
            Text("Overall Result", color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, fontSize = 16.sp)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(70.dp)) {
                ResultCheckbox("Grade", overallGrade, { overallGrade = it })
                ResultCheckbox("Rank", overallRank, { overallRank = it })
                ResultCheckbox("Marks", overallMarks, { overallMarks = it })
            }

            AppDialogDivider()
            ExamDialogSectionHeader("3", "Subject Result Configuration")
            Text("Select the result type required for each subject.", color = AppDialogDefaultColors.muted, fontSize = 14.sp)
            if (isSubjectsLoading) Text("Loading subjects for the selected class…", color = AppMuted, modifier = Modifier.padding(vertical = 12.dp))
            else if (subjects.isEmpty()) Text("No subjects are linked to the selected class.", color = AppMuted, modifier = Modifier.padding(vertical = 12.dp))
            else ExamSubjectResultTable(
                subjects = subjects,
                allowRemove = !editing,
                showGrade = overallGrade,
                showRank = overallRank,
                showMarks = overallMarks,
                onToggle = { index, type ->
                    subjects = subjects.mapIndexed { i, item ->
                        if (i != index) item
                        else {
                            val updated = when (type) {
                                "grade" -> item.copy(grade = !item.grade)
                                "rank" -> item.copy(rank = !item.rank)
                                else -> item.copy(
                                    marks = !item.marks,
                                    main = if (!item.marks && bulkTotalMarks.isNotBlank()) bulkTotalMarks else item.main
                                )
                            }
                            // A result-type checkbox is also the subject
                            // selection control for the exam payload.
                            updated.copy(selected = updated.grade || updated.rank || updated.marks)
                        }
                    }
                },
                onMarksChanged = { index, value ->
                    subjects = subjects.mapIndexed { i, item ->
                        if (i == index) item.copy(main = value.filter(Char::isDigit).take(3)) else item
                    }
                },
                onRemove = { index -> subjects = subjects.filterIndexed { i, _ -> i != index } },
                onBulkToggle = { type, checked ->
                    subjects = subjects.map { item ->
                        val updated = when (type) {
                            "grade" -> item.copy(grade = checked)
                            "rank" -> item.copy(rank = checked)
                            else -> item.copy(
                                marks = checked,
                                main = if (checked && bulkTotalMarks.isNotBlank()) bulkTotalMarks else item.main
                            )
                        }
                        updated.copy(selected = updated.grade || updated.rank || updated.marks)
                    }
                },
                bulkTotalMarks = bulkTotalMarks,
                onBulkTotalMarksChanged = { value ->
                    val normalized = value.filter(Char::isDigit).take(3)
                    bulkTotalMarks = normalized
                    subjects = subjects.map { item ->
                        if (item.marks) item.copy(main = normalized) else item
                    }
                }
            )
        }
        AppDialogDivider()
        AppDialogFooter(actions = listOf(
            AppDialogFooterAction("Cancel", onDismiss, width = 120.dp, enabled = !isSaving),
            AppDialogFooterAction("Save", {
                hasSubmitted = true
                if (formValid) {
                    if (editing) {
                        val editedSubjectConfigs = subjects
                            .filter { subject ->
                                subject.selected && ((subject.grade && overallGrade) || (subject.rank && overallRank) || (subject.marks && overallMarks))
                            }
    .associate { subject ->
        val total = subject.main.toDoubleOrNull() ?: 0.0
        subject.subjectId to ExamSubjectSelectionUi(
            hasGrade = subject.grade && overallGrade,
            hasRank = subject.rank && overallRank,
            hasMarks = subject.marks && overallMarks,
            internalMark = "0",
            mainMark = total.toString(),
            totalMark = total.toString()
        )
    }
                        val editedMaxMarks = editedSubjectConfigs.values
                            .sumOf { it.totalMark.toDoubleOrNull() ?: 0.0 }
                            .takeIf { it > 0.0 }
                            ?: initialExam!!.maxMarks
                        onEdit(initialExam!!.copy(
                            name = name.trim(),
                            type = type.trim(),
                            classes = selectedClasses.joinToString(", "),
                            startDate = startDate,
                            endDate = endDate,
                            status = status,
                            maxMarks = editedMaxMarks,
                            hasOverallGrade = overallGrade,
                            hasOverallRank = overallRank,
                            hasOverallMarks = overallMarks,
                            subjectConfigs = editedSubjectConfigs
                        ))
                    } else {
                        onAdd(buildExamDraft(name, type, selectedClasses, startDate, endDate, status, classOptions, subjects, overallGrade, overallRank, overallMarks))
                    }
                }
            }, AppDialogActionStyle.Primary, width = 120.dp, enabled = !isSaving)
        ), colors = AppDialogDefaultColors, footerHeight = 72.dp)
    }
}

@Composable
private fun ExamDialogSectionHeader(number: String, title: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Box(Modifier.size(40.dp).background(AppPrimarySoft, RoundedCornerShape(7.dp)), contentAlignment = Alignment.Center) { Text(number, color = AppPrimary, fontWeight = FontWeight.Bold, fontSize = 18.sp) }
        AppDialogSectionLabel(title, fontSize = 18.sp)
    }
}

@Composable
private fun ExamNameField(value: String, modifier: Modifier = Modifier, error: String = "", onChange: (String) -> Unit) = AppDialogLabeledTextField("Exam Name", value, onChange, "Select exam name eg: Unit Test 1", modifier, required = true, error = error)

@Composable
private fun ExamTypeField(value: String, modifier: Modifier = Modifier, error: String = "", onChange: (String) -> Unit) =
    AppDialogLabeledTextField("Exam Type", value, onChange, "Select exam Type eg: Unit Test", modifier, required = true, error = error)

@Composable
private fun ExamClassesField(values: List<String>, expanded: Boolean, options: List<String>, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, onSelected: (List<String>) -> Unit) {
    AppDialogFieldColumn("Applicable Classes", required = true, modifier = modifier) {
        AppDialogMultiSelectField(values, "Select classes", options, expanded, onExpand, onDismiss, onSelected, colors = AppDialogDefaultColors)
    }
}

@Composable
private fun ExamClassField(value: String, expanded: Boolean, options: List<String>, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, onSelected: (String) -> Unit) {
    AppDialogFieldColumn("Class", required = true, modifier = modifier) {
        AppDialogSelectField(value, "Select class", expanded, options, onExpand, onDismiss, onSelected, colors = AppDialogDefaultColors)
    }
}

@Composable
private fun ResultCheckbox(label: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Checkbox(checked = checked, onCheckedChange = onCheckedChange, colors = CheckboxDefaults.colors(checkedColor = AppPrimary))
        Text(label, color = AppDialogDefaultColors.navy, fontSize = 15.sp)
    }
}

@Composable
private fun ExamSubjectResultTable(
    subjects: List<ExamSubjectRowDraft>,
    allowRemove: Boolean,
    showGrade: Boolean,
    showRank: Boolean,
    showMarks: Boolean,
    onToggle: (Int, String) -> Unit,
    onMarksChanged: (Int, String) -> Unit,
    onRemove: (Int) -> Unit,
    onBulkToggle: (String, Boolean) -> Unit,
    bulkTotalMarks: String,
    onBulkTotalMarksChanged: (String) -> Unit
) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(6.dp))) {
        Row(Modifier.fillMaxWidth().background(AppSecondaryBackground).padding(horizontal = 14.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
            Text("Subject", Modifier.weight(1.5f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold)
            if (showGrade) ExamResultTypeHeader("Grade", subjects.map { it.grade }, Modifier.weight(.7f)) { onBulkToggle("grade", it) }
            if (showRank) ExamResultTypeHeader("Rank", subjects.map { it.rank }, Modifier.weight(.7f)) { onBulkToggle("rank", it) }
            if (showMarks) {
                ExamResultTypeHeader("Marks", subjects.map { it.marks }, Modifier.weight(.8f)) { onBulkToggle("marks", it) }
                AppDialogNumberField(
                    bulkTotalMarks,
                    onBulkTotalMarksChanged,
                    "Total Marks",
                    Modifier.weight(1f),
                    colors = AppDialogDefaultColors,
                    fieldHeight = 40.dp
                )
            }
            Spacer(Modifier.width(40.dp))
        }
        subjects.forEachIndexed { index, subject ->
            Row(Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 5.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(subject.name, Modifier.weight(1.5f), color = AppDialogDefaultColors.navy, fontSize = 14.sp)
                if (showGrade) Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) { Checkbox(subject.grade, { onToggle(index, "grade") }, colors = CheckboxDefaults.colors(checkedColor = AppPrimary)) }
                if (showRank) Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) { Checkbox(subject.rank, { onToggle(index, "rank") }, colors = CheckboxDefaults.colors(checkedColor = AppPrimary)) }
                if (showMarks) {
                    Box(Modifier.weight(.8f), contentAlignment = Alignment.Center) { Checkbox(subject.marks, { onToggle(index, "marks") }, colors = CheckboxDefaults.colors(checkedColor = AppPrimary)) }
                    if (subject.marks) {
                        AppDialogNumberField(subject.main, { onMarksChanged(index, it) }, "", Modifier.weight(1f), colors = AppDialogDefaultColors, fieldHeight = 40.dp)
                    } else {
                        Text("—", Modifier.weight(1f), color = AppDialogDefaultColors.navy, fontSize = 14.sp, textAlign = TextAlign.Center)
                    }
                }
                if (allowRemove) {
                    IconButton(onClick = { onRemove(index) }, modifier = Modifier.size(40.dp)) {
                        Icon(painterResource(Res.drawable.trash), "Remove ${subject.name} from this exam", tint = AppDanger, modifier = Modifier.size(17.dp))
                    }
                } else Spacer(Modifier.width(40.dp))
            }
            if (index != subjects.lastIndex) HorizontalDivider(color = AppBorder)
        }
    }
}

@Composable
private fun ExamResultTypeHeader(
    label: String,
    checkedRows: List<Boolean>,
    modifier: Modifier = Modifier,
    onCheckedChange: (Boolean) -> Unit
) {
    val selectedCount = checkedRows.count { it }
    val state = when {
        selectedCount == 0 -> ToggleableState.Off
        selectedCount == checkedRows.size -> ToggleableState.On
        else -> ToggleableState.Indeterminate
    }
    Column(modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(label, color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center)
        TriStateCheckbox(
            state = state,
            onClick = { onCheckedChange(state != ToggleableState.On) },
            colors = CheckboxDefaults.colors(checkedColor = AppPrimary)
        )
    }
}

@Composable
private fun ExamStatusField(value: String, expanded: Boolean, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, enabled: Boolean = true, onSelected: (String) -> Unit) {
    AppDialogFieldColumn("Status", modifier = modifier) {
        Surface(
            modifier = Modifier.fillMaxWidth().height(AppDialogFieldHeight),
            shape = AppDialogFieldShape,
            color = AppSecondaryBackground,
            border = androidx.compose.foundation.BorderStroke(1.dp, AppBorder)
        ) {
            Row(Modifier.fillMaxSize().padding(horizontal = 18.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(value, color = examStatusColor(value), fontSize = 18.sp, fontWeight = FontWeight.Medium)
            }
        }
    }
}

private fun examStatusColor(status: String): Color = when (status) {
    "Ready", "Completed" -> AppSuccess
    "Ongoing" -> Color(0xFFF07A12)
    else -> AppPrimary
}

@Composable
private fun ExamSelectField(label: String, value: String, options: List<String>, expanded: Boolean, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier, required: Boolean, onSelected: (String) -> Unit, enabled: Boolean = true) {
    AppDialogFieldColumn(label, required = required, modifier = modifier) { AppDialogSelectField(value, "Select", expanded, options, if (enabled) onExpand else ({}), onDismiss, onSelected, colors = AppDialogDefaultColors) }
}

@Composable
private fun ExamDateField(label: String, value: String, modifier: Modifier = Modifier, error: String = "", onChange: (String) -> Unit) = AppDialogFieldColumn(label, required = true, error = error, modifier = modifier) { AppDialogDateField(value, onChange, colors = AppDialogDefaultColors, placeholder = "DD MMM YYYY", error = error) }

@Composable
private fun ExamQuickFill(internal: String, onInternal: (String) -> Unit, main: String, onMain: (String) -> Unit, onApply: () -> Unit, onClear: () -> Unit) {
    Row(Modifier.fillMaxWidth().background(AppPrimarySoft, RoundedCornerShape(8.dp)).padding(horizontal = 14.dp, vertical = 12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
        Text("Maximum Marks (Quick Fill)", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 14.sp)
        Text("Internal", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        AppDialogNumberField(internal, onInternal, "", Modifier.width(110.dp), colors = AppDialogDefaultColors, fieldHeight = 42.dp)
        Text("Main", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        AppDialogNumberField(main, onMain, "", Modifier.width(110.dp), colors = AppDialogDefaultColors, fieldHeight = 42.dp)
        AppButton(onClick = onApply, modifier = Modifier.width(104.dp).height(44.dp), contentPadding = PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.buttonColors(containerColor = AppPrimary, contentColor = Color.White), shape = RoundedCornerShape(8.dp)) { Text("Apply", fontWeight = FontWeight.Bold) }
        AppOutlinedButton(onClick = onClear, modifier = Modifier.width(104.dp).height(44.dp), contentPadding = PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) { Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp), tint = AppPrimary); Spacer(Modifier.width(6.dp)); Text("Clear", color = AppPrimary) }
    }
}

@Composable
private fun ExamSubjectTable(subjects: List<ExamSubjectRowDraft>, onToggle: (Int) -> Unit, onInternalChanged: (Int, String) -> Unit, onMainChanged: (Int, String) -> Unit) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(6.dp))) {
        Row(Modifier.fillMaxWidth().background(AppPrimarySoft).padding(horizontal = 10.dp, vertical = 9.dp), verticalAlignment = Alignment.CenterVertically) { Text("#", Modifier.width(54.dp), color = AppPrimary, fontWeight = FontWeight.Bold); Text("Subject", Modifier.weight(1f), color = AppPrimary, fontWeight = FontWeight.Bold); Text("Internal", Modifier.width(150.dp), color = AppPrimary, fontWeight = FontWeight.Bold); Text("Main", Modifier.width(150.dp), color = AppPrimary, fontWeight = FontWeight.Bold); Text("Total", Modifier.width(110.dp), color = AppPrimary, fontWeight = FontWeight.Bold) }
        if (subjects.isEmpty()) {
            Text("Select applicable classes to load subjects.", color = AppMuted, fontSize = 13.sp, modifier = Modifier.padding(16.dp))
        }
        subjects.forEachIndexed { index, subject ->
            Row(Modifier.fillMaxWidth().border(0.dp, Color.Transparent).padding(horizontal = 10.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Checkbox(subject.selected, { onToggle(index) }, Modifier.width(54.dp), colors = CheckboxDefaults.colors(checkedColor = AppPrimary))
                Text(subject.name, Modifier.weight(1f), color = AppNavy, fontSize = 13.sp)
                if (subject.selected) {
                    AppDialogNumberField(subject.internal, { onInternalChanged(index, it) }, "", Modifier.width(150.dp), colors = AppDialogDefaultColors, fieldHeight = 38.dp)
                    Spacer(Modifier.width(10.dp))
                    AppDialogNumberField(subject.main, { onMainChanged(index, it) }, "", Modifier.width(150.dp), colors = AppDialogDefaultColors, fieldHeight = 38.dp)
                    Spacer(Modifier.width(10.dp))
                    Box(Modifier.width(110.dp).height(38.dp).background(AppSecondaryBackground, RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) { Text(subject.total, color = AppNavy, fontWeight = FontWeight.Medium) }
                } else {
                    Text("–", Modifier.width(150.dp), color = AppMuted); Spacer(Modifier.width(10.dp)); Text("–", Modifier.width(150.dp), color = AppMuted); Spacer(Modifier.width(10.dp)); Text("–", Modifier.width(110.dp), color = AppMuted)
                }
            }
        }
    }
}

@Composable
internal fun ExamViewDialog(
    row: ExamRowUi?,
    subjectNamesById: Map<String, String> = emptyMap(),
    isLoading: Boolean = false,
    errorMessage: String = "",
    onDismiss: () -> Unit
) {
    row ?: return
    val selectedSubjects = row.subjectConfigs.keys.toList()
    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Wide,
        maxDialogHeight = 1000.dp,
        isLoading = isLoading,
        loadingTitle = "Loading exam details"
    ) {
        AppDialogHeader(title = "View Exam Details", subtitle = "View the exam and its result configuration.", icon = null, onClose = onDismiss, colors = AppDialogDefaultColors)
        if (errorMessage.isNotBlank()) {
            AppDialogErrorMessage(errorMessage, modifier = Modifier.padding(horizontal = 28.dp, vertical = 14.dp))
        }
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 28.dp, vertical = 20.dp), verticalArrangement = Arrangement.spacedBy(18.dp)) {
            ExamDialogSectionHeader("1", "Examination Details")
            AppDialogFormGrid(Modifier.fillMaxWidth()) { compact ->
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        ExamReadOnlyInfo("Exam Name", row.name)
                        ExamReadOnlyInfo("Exam Type", row.type)
                        ExamReadOnlyInfo("Start Date", row.startDate)
                        ExamReadOnlyInfo("End Date", row.endDate.ifBlank { "—" })
                        ExamReadOnlyInfo("Class", row.classes)
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                        Row(horizontalArrangement = Arrangement.spacedBy(70.dp)) { ExamReadOnlyInfo("Exam Name", row.name, Modifier.weight(1f)); ExamReadOnlyInfo("Exam Type", row.type, Modifier.weight(1f)) }
                        Row(horizontalArrangement = Arrangement.spacedBy(70.dp)) { ExamReadOnlyInfo("Start Date", row.startDate, Modifier.weight(1f)); ExamReadOnlyInfo("End Date", row.endDate.ifBlank { "—" }, Modifier.weight(1f)); ExamReadOnlyInfo("Class", row.classes, Modifier.weight(1f)) }
                    }
                }
            }

            AppDialogDivider()
            ExamDialogSectionHeader("2", "Result Configuration")
            Text("Choose how the overall result will be recorded and displayed.", color = AppDialogDefaultColors.muted, fontSize = 14.sp)
            Text("Overall Result", color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, fontSize = 16.sp)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(70.dp)) {
                ReadOnlyResultCheckbox("Grade", row.hasOverallGrade)
                ReadOnlyResultCheckbox("Rank", row.hasOverallRank)
                ReadOnlyResultCheckbox("Marks", row.hasOverallMarks)
            }

            AppDialogDivider()
            ExamDialogSectionHeader("3", "Subject Result Configuration")
            Text("Select the result type required for each subject.", color = AppDialogDefaultColors.muted, fontSize = 14.sp)
            ExamReadOnlySubjectResultTable(selectedSubjects, row.subjectConfigs.values.toList(), subjectNamesById)
        }
        AppDialogDivider()
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Primary, width = 120.dp)), colors = AppDialogDefaultColors, footerHeight = 72.dp)
    }
}

@Composable
private fun ExamReadOnlyInfo(label: String, value: String, modifier: Modifier = Modifier) {
    AppDialogFieldColumn(label, modifier = modifier) {
        Text(value.ifBlank { "—" }, color = AppDialogDefaultColors.navy, fontSize = 16.sp, fontWeight = FontWeight.Medium, modifier = Modifier.padding(horizontal = 8.dp, vertical = 8.dp))
    }
}

@Composable
private fun ReadOnlyResultCheckbox(label: String, checked: Boolean) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Checkbox(checked = checked, onCheckedChange = null, enabled = false, colors = CheckboxDefaults.colors(checkedColor = AppPrimary, disabledCheckedColor = AppPrimary))
        Text(label, color = AppDialogDefaultColors.navy, fontSize = 15.sp)
    }
}

@Composable
private fun ExamReadOnlySubjectResultTable(
    subjects: List<String>,
    configs: List<ExamSubjectSelectionUi>,
    subjectNamesById: Map<String, String>
) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(6.dp))) {
        Row(Modifier.fillMaxWidth().background(AppSecondaryBackground).padding(horizontal = 14.dp, vertical = 11.dp), verticalAlignment = Alignment.CenterVertically) {
            Text("Subject", Modifier.weight(1.5f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold)
            Text("Grade", Modifier.weight(.7f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center)
            Text("Rank", Modifier.weight(.7f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center)
            Text("Marks", Modifier.weight(.8f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center)
            Text("Total Marks", Modifier.weight(1f), color = AppDialogDefaultColors.navy, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center)
        }
        subjects.forEachIndexed { index, subject ->
            val config = configs.getOrNull(index)
            val grade = config?.hasGrade == true
            val rank = config?.hasRank == true
            val marks = config?.hasMarks == true
            Row(Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 5.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    subjectNamesById[subject]?.trim()?.takeIf(String::isNotBlank) ?: "Subject name unavailable",
                    Modifier.weight(1.5f),
                    color = AppDialogDefaultColors.navy,
                    fontSize = 14.sp
                )
                Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) { Checkbox(grade, null, enabled = false, colors = CheckboxDefaults.colors(checkedColor = AppPrimary, disabledCheckedColor = AppPrimary)) }
                Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) { Checkbox(rank, null, enabled = false, colors = CheckboxDefaults.colors(checkedColor = AppPrimary, disabledCheckedColor = AppPrimary)) }
                Box(Modifier.weight(.8f), contentAlignment = Alignment.Center) { Checkbox(marks, null, enabled = false, colors = CheckboxDefaults.colors(checkedColor = AppPrimary, disabledCheckedColor = AppPrimary)) }
                Text(if (marks) config?.totalMark?.ifBlank { "—" } ?: "—" else "—", Modifier.weight(1f), color = AppDialogDefaultColors.navy, fontSize = 14.sp, textAlign = androidx.compose.ui.text.style.TextAlign.Center)
            }
            if (index != subjects.lastIndex) HorizontalDivider(color = AppBorder)
        }
        if (subjects.isEmpty()) Text("No subjects configured for the selected class.", color = AppMuted, modifier = Modifier.padding(16.dp))
    }
}

@Composable
internal fun ExamResultsViewDialog(
    row: ExamRowUi?,
    results: List<ExamResultPreviewUi>,
    isLoading: Boolean = false,
    errorMessage: String = "",
    isDownloading: Boolean = false,
    downloadError: String = "",
    onDownloadExcel: (String) -> Unit = {},
    onDismiss: () -> Unit
) {
    if (row == null) return
    var query by remember(row.id) { mutableStateOf("") }
    val filteredResults = results.filter { result ->
        val q = query.trim()
        q.isBlank() || result.studentName.contains(q, true) || result.rollNumber.contains(q, true)
    }
    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Wide,
        maxDialogHeight = 820.dp,
        isLoading = isLoading || isDownloading,
        loadingTitle = if (isDownloading) "Preparing Excel download" else "Loading results"
    ) {
        AppDialogHeader(title = "View Results", subtitle = "View published student results for this exam.", icon = Res.drawable.clipboard_check, onClose = onDismiss, colors = AppDialogDefaultColors)
        Row(Modifier.fillMaxWidth().padding(horizontal = 28.dp, vertical = 14.dp), horizontalArrangement = Arrangement.spacedBy(18.dp)) {
            ResultDialogExamInfo("Exam ID", row.id, Modifier.weight(.8f))
            ResultDialogExamInfo("Exam Name", row.name, Modifier.weight(1.3f))
            ResultDialogExamInfo("Exam Type", row.type, Modifier.weight(1f))
            ResultDialogExamInfo("Class", row.classes, Modifier.weight(.8f))
            ResultDialogExamInfo("Start Date", row.startDate, Modifier.weight(1f))
            ResultDialogExamInfo("Status", row.status, Modifier.weight(.8f), valueColor = if (row.status == "Published") AppPrimary else AppDanger)
        }
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).padding(horizontal = 20.dp, vertical = 14.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                AdminListSearchField(query, { query = it }, "Search by student name or roll no.", width = 370.dp, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy)
                AppOutlinedButton({ onDownloadExcel(row.backendId) }, Modifier.width(190.dp).height(44.dp), enabled = !isDownloading, colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) {
                    Icon(painterResource(Res.drawable.download), null, Modifier.size(18.dp), tint = AppPrimary)
                    Spacer(Modifier.width(8.dp))
                    Text("Download Excel", color = AppPrimary, fontSize = 13.sp)
                }
            }
            if (downloadError.isNotBlank()) {
                Text("Unable to download Excel: $downloadError", color = AppDanger, fontSize = 12.sp)
            }
            Row(Modifier.fillMaxWidth().background(AppInfoBg, RoundedCornerShape(6.dp)).border(1.dp, AppPrimary.copy(alpha = .16f), RoundedCornerShape(6.dp)).padding(horizontal = 14.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(painterResource(Res.drawable.info_circle), null, tint = AppPrimary, modifier = Modifier.size(19.dp))
                Text("AB indicates that the student was absent for that subject. Absent subjects are excluded from applicable totals and percentage calculations.", color = AppNavy, fontSize = 12.sp)
            }
            when {
                isLoading -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("Loading published results…", color = AppMuted) }
                errorMessage.isNotBlank() -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { AppDialogErrorMessage("Unable to load results: $errorMessage") }
                filteredResults.isEmpty() -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("No published results found.", color = AppMuted) }
                else -> ExamResultsTable(filteredResults)
            }
        }
        AppDialogDivider()
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Primary, width = 120.dp)), colors = AppDialogDefaultColors, footerHeight = 72.dp)
    }
}

@Composable
private fun ResultDialogExamInfo(label: String, value: String, modifier: Modifier = Modifier, valueColor: Color = AppNavy) {
    Column(modifier.padding(vertical = 2.dp), verticalArrangement = Arrangement.spacedBy(5.dp)) {
        Text(label, color = AppMuted, fontSize = 12.sp)
        Text(value.ifBlank { "—" }, color = valueColor, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}

@Composable
private fun ExamResultsTable(results: List<ExamResultPreviewUi>) {
    val scroll = rememberScrollState()
    val verticalScroll = rememberScrollState()
    val subjectNames = results.flatMap { it.subjects }.map { it.name }.filter(String::isNotBlank).distinct()
    val rollWidth = 100.dp
    val nameWidth = 220.dp
    val overallWidth = 100.dp
    val subjectWidth = 210.dp
    val notesWidth = 300.dp
    val tableWidth = rollWidth + nameWidth + overallWidth * 4 + subjectWidth * subjectNames.size + notesWidth
    Column(Modifier.fillMaxWidth().height(570.dp).border(1.dp, AppBorder, RoundedCornerShape(8.dp)).horizontalScroll(scroll)) {
        Column(Modifier.width(tableWidth).background(AppSoftSurface)) {
            Row(Modifier.height(36.dp), verticalAlignment = Alignment.CenterVertically) {
                Spacer(Modifier.width(rollWidth + nameWidth))
                ResultGroupHeader("Overall Result", overallWidth * 4)
                ResultGroupHeader("Subject Results", subjectWidth * subjectNames.size)
                ResultGroupHeader("Notes", notesWidth)
            }
            Row(Modifier.height(44.dp), verticalAlignment = Alignment.CenterVertically) {
                ResultHeaderCell("Roll No.", rollWidth, rowSpan = 2)
                ResultHeaderCell("Student Name", nameWidth, rowSpan = 2)
                listOf("Grade", "Rank", "Total Marks", "% Marks").forEach { ResultHeaderCell(it, overallWidth) }
                subjectNames.forEach { ResultHeaderCell(it, subjectWidth) }
                Spacer(Modifier.width(notesWidth))
            }
            Row(Modifier.height(38.dp), verticalAlignment = Alignment.CenterVertically) {
                Spacer(Modifier.width(rollWidth + nameWidth + overallWidth * 4))
                subjectNames.forEach { SubjectResultHeader(subjectWidth) }
                Spacer(Modifier.width(notesWidth))
            }
        }
        Column(Modifier.width(tableWidth).weight(1f).verticalScroll(verticalScroll)) {
            results.forEachIndexed { index, result ->
                Row(Modifier.width(tableWidth).height(54.dp), verticalAlignment = Alignment.CenterVertically) {
                    ResultTableCell(result.rollNumber, rollWidth)
                    ResultTableCell(result.studentName, nameWidth, FontWeight.SemiBold)
                    ResultTableCell(result.grade, overallWidth)
                    ResultTableCell(result.rank, overallWidth)
                    ResultTableCell(result.totalMarks, overallWidth)
                    ResultTableCell(result.percentage, overallWidth)
                    subjectNames.forEach { subjectName ->
                        val subject = result.subjects.firstOrNull { it.name == subjectName }
                        SubjectResultCells(subject, subjectWidth)
                    }
                    ResultTableCell(result.notes, notesWidth)
                }
                if (index != results.lastIndex) HorizontalDivider(color = AppBorder, modifier = Modifier.width(tableWidth))
            }
        }
    }
}

@Composable
private fun RowScope.ResultGroupHeader(text: String, width: Dp) {
    Box(Modifier.width(width).fillMaxHeight(), contentAlignment = Alignment.Center) { Text(text, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 12.sp) }
}

@Composable
private fun RowScope.ResultHeaderCell(text: String, width: Dp, rowSpan: Int = 1) {
    Box(Modifier.width(width).fillMaxHeight().border(1.dp, AppBorder), contentAlignment = Alignment.Center) { Text(text, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp, textAlign = androidx.compose.ui.text.style.TextAlign.Center) }
}

@Composable
private fun RowScope.SubjectResultHeader(width: Dp) {
    val cellWidth = width / 3
    Row(Modifier.width(width).fillMaxHeight()) {
        listOf("Marks", "Total", "%").forEach { label ->
            Box(Modifier.width(cellWidth).fillMaxHeight().border(1.dp, AppBorder), contentAlignment = Alignment.Center) {
                Text(label, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 11.sp)
            }
        }
    }
}

@Composable
private fun RowScope.SubjectResultCells(subject: ExamSubjectPreviewUi?, width: Dp) {
    val cellWidth = width / 3
    Row(Modifier.width(width).fillMaxHeight()) {
        ResultTableCell(subject?.marks.orEmpty(), cellWidth)
        ResultTableCell(subject?.totalMarks.orEmpty(), cellWidth)
        ResultTableCell(subject?.percentage.orEmpty(), cellWidth)
    }
}

@Composable
private fun ResultTableCell(text: String, width: Dp, weight: FontWeight = FontWeight.Normal, alignment: Alignment = Alignment.Center) {
    Box(Modifier.width(width).fillMaxHeight().border(1.dp, AppBorder), contentAlignment = alignment) {
        Text(text, color = AppNavy, fontSize = 12.sp, fontWeight = weight, maxLines = 1)
    }
}

@Composable
internal fun ExamInsightsDialog(
    row: ExamRowUi?,
    insights: ExamInsightsUi?,
    isLoading: Boolean = false,
    errorMessage: String = "",
    onDismiss: () -> Unit
) {
    if (row == null) return
    val insightBuckets = insights?.buckets.orEmpty()
    val bucketColors = listOf(AppPrimary, Color(0xFF2166F3), Color(0xFF63A6EF), AppOrangeText, AppDanger)
    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, maxDialogHeight = 820.dp, isLoading = isLoading, loadingTitle = "Loading exam insights") {
        AppDialogHeader(title = "Exam Insights", subtitle = "Analyze class performance and identify students who need support.", icon = Res.drawable.health_chart, onClose = onDismiss, colors = AppDialogDefaultColors)
        Row(Modifier.fillMaxWidth().padding(horizontal = 28.dp, vertical = 14.dp), horizontalArrangement = Arrangement.spacedBy(22.dp)) {
            ResultDialogExamInfo("Exam ID", row.id, Modifier.weight(.9f))
            ResultDialogExamInfo("Exam Name", row.name, Modifier.weight(1.5f))
            ResultDialogExamInfo("Class", insights?.classLabel ?: row.classes, Modifier.weight(.8f))
            ResultDialogExamInfo("Start Date", row.startDate, Modifier.weight(1f))
            ResultDialogExamInfo("Students", insights?.totalStudents?.toString() ?: "—", Modifier.weight(.7f))
            ResultDialogExamInfo("Status", insights?.status ?: "—", Modifier.weight(.9f), if (insights?.status.equals("Published", true)) AppSuccess else AppMuted)
        }
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 22.dp, vertical = 18.dp), verticalArrangement = Arrangement.spacedBy(18.dp)) {
            when {
                errorMessage.isNotBlank() -> AppDialogErrorMessage("Unable to load exam insights: $errorMessage")
                insights == null && !isLoading -> Text("No insights are available for this exam.", color = AppMuted, fontSize = 13.sp)
            }
            Text("Overall Class Performance", color = AppNavy, fontSize = 21.sp, fontWeight = FontWeight.SemiBold)
            AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
                Row(Modifier.fillMaxWidth().height(150.dp), horizontalArrangement = Arrangement.SpaceEvenly) {
                    insightBuckets.forEachIndexed { index, band ->
                        Column(Modifier.weight(1f).fillMaxHeight().padding(horizontal = 16.dp, vertical = 18.dp), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            Text(band.label, color = AppNavy, fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
                            Box(Modifier.fillMaxWidth().height(13.dp).background(AppSoftSurface, RoundedCornerShape(8.dp))) { Box(Modifier.fillMaxWidth((band.count.toFloat() / (insights?.totalStudents ?: 1)).coerceIn(.06f, 1f)).fillMaxHeight().background(bucketColors.getOrElse(index) { AppPrimary }, RoundedCornerShape(8.dp))) }
                            Text("${band.count} students (${band.percentage})", color = AppNavy, fontSize = 12.sp)
                        }
                        if (index != insightBuckets.lastIndex) Box(Modifier.width(1.dp).height(105.dp).background(AppBorder))
                    }
                }
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                InsightStudentsCard("Top 5 Students", insights?.topStudents.orEmpty(), Modifier.weight(1f))
                InsightStudentsCard("Needs Improvement – 5 Students", insights?.bottomStudents.orEmpty(), Modifier.weight(1f))
            }
        }
        AppDialogDivider()
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Primary, width = 120.dp)), colors = AppDialogDefaultColors, footerHeight = 72.dp)
    }
}

@Composable
private fun InsightStudentsCard(title: String, students: List<ExamInsightStudentUi>, modifier: Modifier) {
    AppCard(modifier.border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Text(title, Modifier.padding(horizontal = 18.dp, vertical = 16.dp), color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
        Row(Modifier.fillMaxWidth().background(AppSoftSurface).padding(horizontal = 12.dp, vertical = 12.dp)) {
            InsightCell("Rank", 65.dp, true); InsightCell("Roll No.", 85.dp, true); InsightCell("Student Name", 0.dp, true, true); InsightCell("Total Marks", 105.dp, true); InsightCell("% Marks", 90.dp, true)
        }
        students.forEachIndexed { index, student ->
            Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 13.dp)) {
                InsightCell(student.rank, 65.dp); InsightCell(student.rollNumber, 85.dp); InsightCell(student.name, 0.dp, flexible = true); InsightCell(student.totalMarks, 105.dp); InsightCell(student.percentage, 90.dp)
            }
            if (index != students.lastIndex) HorizontalDivider(color = AppBorder)
        }
    }
}

@Composable
private fun RowScope.InsightCell(text: String, width: Dp, header: Boolean = false, flexible: Boolean = false) {
    if (flexible) Text(text, Modifier.weight(1f), color = AppNavy, fontSize = 12.sp, fontWeight = if (header) FontWeight.SemiBold else FontWeight.Normal, maxLines = 1)
    else Text(text, Modifier.width(width), color = AppNavy, fontSize = 12.sp, fontWeight = if (header) FontWeight.SemiBold else FontWeight.Normal, maxLines = 1)
}


@Composable
private fun ExamAuditCard() {
    Surface(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)), color = Color.White, shape = RoundedCornerShape(8.dp)) {
        Row(Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 16.dp), verticalAlignment = Alignment.CenterVertically) {
            ExamAuditPerson("Created By", "Ravi Kumar", "18 Aug 2026, 09:30 AM", Modifier.weight(1f))
            Box(Modifier.width(1.dp).height(64.dp).background(AppBorder))
            ExamAuditPerson("Updated By", "Ravi Kumar", "20 Aug 2026, 03:45 PM", Modifier.weight(1f))
        }
    }
}

@Composable
private fun ExamAuditPerson(label: String, name: String, timestamp: String, modifier: Modifier) {
    Row(modifier.padding(horizontal = 4.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Box(Modifier.size(38.dp).background(AppPrimarySoft, RoundedCornerShape(50)), contentAlignment = Alignment.Center) {
            Icon(painterResource(Res.drawable.user), contentDescription = null, tint = AppPrimary, modifier = Modifier.size(20.dp))
        }
        Column(verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(label, color = AppNavy, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
            Text(name, color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 14.sp)
            Text(timestamp, color = AppMuted, fontSize = 12.sp)
        }
    }
}

@Composable
private fun ExamViewInfoCard(row: ExamRowUi) {
    Surface(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)), color = Color.White, shape = RoundedCornerShape(8.dp)) {
        Column(Modifier.padding(18.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Examination Information", color = AppPrimary, fontWeight = FontWeight.Bold, fontSize = 17.sp)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(30.dp)) {
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    ExamInfoLine("Exam Name", row.name)
                    ExamInfoLine("Applicable Classes", row.classes)
                    ExamInfoLine("Exam Start Date", row.startDate)
                }
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    ExamInfoLine("Exam Type", row.type)
                    ExamInfoLine("Status", row.status, examStatusColor(row.status))
                    ExamInfoLine("Exam End Date", row.endDate.ifBlank { "—" })
                }
            }
        }
    }
}

@Composable
private fun ExamInfoLine(label: String, value: String, valueColor: Color = AppNavy) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(label, Modifier.weight(1f), color = AppNavy, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        Text(":", color = AppNavy, fontSize = 13.sp)
        Text(value, Modifier.weight(1.4f), color = valueColor, fontWeight = FontWeight.Medium, fontSize = 13.sp)
    }
}

@Composable
private fun ExamReadOnlySubjectTable(subjects: List<String>) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(6.dp))) {
        Row(Modifier.fillMaxWidth().background(AppPrimarySoft).padding(horizontal = 12.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically) {
            Text("#", Modifier.width(54.dp), color = AppPrimary, fontWeight = FontWeight.Bold)
            Text("Subject", Modifier.weight(1f), color = AppPrimary, fontWeight = FontWeight.Bold)
            Text("Internal", Modifier.width(150.dp), color = AppPrimary, fontWeight = FontWeight.Bold)
            Text("Main", Modifier.width(150.dp), color = AppPrimary, fontWeight = FontWeight.Bold)
            Text("Total", Modifier.width(110.dp), color = AppPrimary, fontWeight = FontWeight.Bold)
        }
        subjects.forEachIndexed { index, subject ->
            val internal = if (index == 0 || index == 2) 20 else 0
            val main = if (index == 0 || index == 2) 80 else 100
            Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 11.dp), verticalAlignment = Alignment.CenterVertically) {
                Text((index + 1).toString(), Modifier.width(54.dp), color = AppNavy, fontSize = 13.sp)
                Text(subject, Modifier.weight(1f), color = AppNavy, fontSize = 13.sp)
                Text(internal.toString(), Modifier.width(150.dp), color = AppNavy, fontSize = 13.sp)
                Text(main.toString(), Modifier.width(150.dp), color = AppNavy, fontSize = 13.sp)
                Text((internal + main).toString(), Modifier.width(110.dp), color = AppNavy, fontSize = 13.sp)
            }
            if (index != subjects.lastIndex) HorizontalDivider(color = AppBorder)
        }
        if (subjects.isEmpty()) Text("No subjects configured for the selected classes.", color = AppMuted, modifier = Modifier.padding(16.dp))
    }
}
